import * as React from 'react';
import { Button, Input, Modal, Select } from '../..';
import useValidator from '../../../hooks/useValidator';
import { Discount } from '../../../pages/POSTeller/POSTeller';

type ModalProps = {
  onCloseModal: () => void;
  onSetAddDiscount: (discount: Discount) => void;
  itemId: number;
  currencySymbol: string;
};

function DiscountAdd({
  onCloseModal,
  onSetAddDiscount,
  itemId,
  currencySymbol,
}: ModalProps) {
  const [discount, setDiscount] = React.useState({
    type: 'fixed-amount',
    value: NaN,
    couponCode: '',
  });
  const [errors, setErrors] = React.useState({
    type: false,
    value: false,
  });
  const [validator, showValidationMessage] = useValidator({});
  const [valueError, setValueError] = React.useState('');

  const handleDiscountInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = event.target;

    setValueError('');

    setDiscount(fndDiscount => ({
      ...fndDiscount,
      [name]: type !== 'number' ? value : (event.target as any)?.valueAsNumber,
    }));
  };

  const handleAddDiscount = async () => {
    if (!validator.allValid()) {
      setErrors(errs => ({
        ...errs,
        type: !validator.fieldValid('type'),
        value: !validator.fieldValid('value'),
      }));
      showValidationMessage(true);
      return;
    }

    if (discount.value) {
      if (discount.type === 'percentage' && discount.value > 100) {
        setValueError('Percentage off cannot be more than 100');
        return;
      }

      if (discount.value < 0) {
        const type =
          discount.type === 'percentage' ? 'Percentage off' : 'Amount';
        setValueError(`${type} cannot less than 0`);
        return;
      }
    }

    const fndDiscount = {
      ...discount,
      value: discount.value ? discount.value : 0,
    } as Discount;

    onCloseModal();
    onSetAddDiscount(fndDiscount);
  };

  return (
    <Modal onClose={onCloseModal} show>
      <div className="rounded-md bg-white tablet:w-[500px]">
        <form>
          <div className="flex flex-col p-5">
            <h3
              className="text-pos-black text-[20px] font-semibold mb-2
              inline-flex whitespace-pre"
            >
              {itemId && itemId !== 0 ? 'Item Discount' : 'Sale Discount'}
            </h3>
            <div className="text-pos-black text-sm mt-2 mb-2 font-normal space-y-4">
              <div>
                <label htmlFor="type" className="text-xbase block mb-1">
                  Type{' '}
                  <span className="text-xs font-light text-pos-red">*</span>
                </label>
                <Select
                  placeholder={{
                    value: '',
                    label: '',
                  }}
                  value={discount.type}
                  options={[
                    {
                      value: 'fixed-amount',
                      label: 'Fixed Amount',
                    },
                    {
                      value: 'percentage',
                      label: 'Percentage',
                    },
                  ]}
                  name="type"
                  id="type"
                  onChange={handleDiscountInputChange}
                  className="w-full"
                />
              </div>
              <div>
                <label htmlFor="value" className="text-xbase block mb-1">
                  {discount.type === 'fixed-amount'
                    ? 'Amount'
                    : 'Percentage off '}
                  <span className="text-xs font-light text-pos-red">*</span>
                </label>
                <div className="relative">
                  <Input
                    name="value"
                    id="value"
                    value={discount.value}
                    min={0}
                    type="number"
                    placeholder={`${
                      discount.type === 'fixed-amount' ? '0.00' : ''
                    }`}
                    onChange={handleDiscountInputChange}
                    onBlur={() => {
                      setErrors(errs => ({
                        ...errs,
                        value: !validator.check(
                          discount.value,
                          'required|numeric|min:0,num',
                        ),
                      }));
                    }}
                    error={errors.value}
                    className={`sale-quantity ${
                      discount.type === 'fixed-amount' ? 'pl-[25px]' : ''
                    }`}
                  />
                  <span
                    className={`${
                      discount.type === 'fixed-amount'
                        ? 'left-3 text-xbase'
                        : 'right-3 text-base'
                    } absolute top-0 my-auto z-50 bottom-0 text-base font-medium inline-flex items-center`}
                  >
                    {discount.type === 'fixed-amount' ? currencySymbol : '%'}
                  </span>
                </div>
                {valueError && (
                  <div className="text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1">
                    {valueError}
                  </div>
                )}
                {validator.message(
                  'value',
                  discount.value,
                  'required|numeric|min:0,num',
                  {
                    messages: {
                      required: ` ${
                        discount.type === 'fixed-amount'
                          ? 'Amount'
                          : 'Percentage off '
                      } is required.`,
                    },
                    className:
                      'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                  },
                )}
              </div>

              <div className="w-full border-t border-[#d0d7de] !mt-8">
                <h2 className="text-xbase font-semibold inline-flex items-center mt-4">
                  Coupon code
                  <span
                    className="leading-0 text-[10px] w-3 h-3 bg-[#666] rounded-full inline-flex
                    items-center justify-center text-white ml-1 cursor-help -mt-[3px]"
                  >
                    ?
                  </span>
                </h2>
                <Input
                  id="couponCode"
                  name="couponCode"
                  value={discount.couponCode}
                  onChange={handleDiscountInputChange}
                />
                <span className="text-pos-gray font-light text-sm mt-2 inline-block leading-normal">
                  Coupon can also be used to discount items or a sale.
                </span>
              </div>
            </div>
            <div className="mt-5 text-center flex flex-row ml-auto space-x-4">
              <Button
                label="Save"
                onClick={handleAddDiscount}
                type="button"
                disabled={false}
                className="!bg-pos-green border border-[rgba(27,31,36,0.15)] min-w-[150px]  h-[40px]
                      items-center justify-center text-white text-xbase
                      shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)] shrink-0 font-medium"
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default DiscountAdd;
